import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Clock, MapPin } from '@phosphor-icons/react';
import { getLeaveTypeLabel } from '../../utils/styleHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_LABELS = { low: 'Basse', normal: 'Normale', high: 'Haute', urgent: 'Urgente' };
const PRIORITY_BG = { low: '#9CA3AF', normal: '#1565C0', high: '#F59E0B', urgent: '#DC2626' };

const LEAVE_PALETTE = {
  maladie: { bg: '#FFF3E0', border: '#FFB74D', text: '#E65100' },
  absence: { bg: '#F4F5F7', border: '#BDBDBD', text: '#5E5F66' },
  default: { bg: '#E8F5E9', border: '#81C784', text: '#2E7D32' },
};

const TaskCard = ({ task, onClick }) => {
  const isLeave = task.is_leave;
  const palette = isLeave ? (LEAVE_PALETTE[task.leave_type] || LEAVE_PALETTE.default) : null;
  const taskColor = task.color || '#1565C0';
  const taskPriority = task.priority || 'normal';

  return (
    <div
      data-testid={`planning-task-${task.id}`}
      draggable={!isLeave}
      onDragStart={(e) => {
        if (isLeave) return;
        e.dataTransfer.setData('application/json', JSON.stringify({ id: task.id, user_id: task.user_id, date: task.date }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      style={{
        backgroundColor: isLeave ? palette.bg : '#FFFFFF',
        border: `1px solid ${isLeave ? palette.border : 'rgba(0,0,0,0.08)'}`,
        borderRadius: '8px', padding: '8px 8px 8px 10px', marginBottom: '6px', fontSize: '0.75rem',
        cursor: isLeave ? 'default' : 'grab',
        borderLeft: `4px solid ${isLeave ? palette.text : taskColor}`,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        position: 'relative',
      }}
      onClick={() => !isLeave && onClick(task)}
    >
      {!isLeave && taskPriority !== 'normal' && (
        <span title={`Priorite ${PRIORITY_LABELS[taskPriority] || taskPriority}`} style={{ position: 'absolute', top: 6, right: 6, padding: '1px 6px', borderRadius: 999, fontSize: '0.6rem', fontWeight: 700, color: 'white', backgroundColor: PRIORITY_BG[taskPriority] || '#1565C0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {PRIORITY_LABELS[taskPriority] || taskPriority}
        </span>
      )}
      {!isLeave && (task.user_photo_url || task.user_name) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          {task.user_photo_url ? (
            <img src={`${API_URL}${task.user_photo_url}`} alt={task.user_name} style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E2E2E6' }} onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: taskColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700 }}>
              {(task.user_name || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <span style={{ fontSize: '0.65rem', color: '#5E5F66', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.user_name}</span>
        </div>
      )}
      <div style={{ fontWeight: '700', marginBottom: '4px', color: isLeave ? palette.text : '#202124', paddingRight: taskPriority !== 'normal' ? 36 : 0 }}>{task.title}</div>
      {!isLeave && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#5E5F66' }}>
          <Clock size={12} /> {task.start_time} - {task.end_time}
        </div>
      )}
      {isLeave && (
        <div style={{ fontSize: '0.65rem', fontWeight: '600', color: palette.text, textTransform: 'uppercase' }}>
          {getLeaveTypeLabel(task.leave_type)}
        </div>
      )}
      {task.address && !isLeave && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#5E5F66', marginTop: '4px' }}>
          <MapPin size={12} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{task.address}</span>
        </div>
      )}
    </div>
  );
};

export const PlanningWeekView = ({ weekDays, employees, tasks, holidaysByDate, dragOverCell, setDragOverCell, onTaskDrop, onTaskClick, onAddTaskForCell }) => {
  const getTasksForDay = (date, userId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return tasks.filter(t => t.date === dateStr && (!userId || t.user_id === userId));
  };

  return (
    <div className="card">
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ minWidth: '1000px' }}>
          <thead>
            <tr>
              <th style={{ width: '150px', position: 'sticky', left: 0, backgroundColor: '#F4F5F7', zIndex: 1 }}>Employe</th>
              {weekDays.map((day) => {
                const ds = format(day, 'yyyy-MM-dd');
                const holiday = holidaysByDate[ds];
                return (
                  <th key={day.toISOString()} style={{ textAlign: 'center', minWidth: '140px', backgroundColor: holiday ? '#FFF1F2' : undefined }}>
                    <div>{format(day, 'EEEE', { locale: fr })}</div>
                    <div style={{ fontWeight: '400', fontSize: '0.7rem' }}>{format(day, 'dd-MM-yyyy')}</div>
                    {holiday && (
                      <div data-testid={`holiday-${ds}`} style={{ marginTop: '2px', fontSize: '0.65rem', fontWeight: 700, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {holiday}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td style={{ fontWeight: '600', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, borderRight: '1px solid #E2E2E6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {employee.photo_url ? (
                      <img
                        src={employee.photo_url.startsWith('http') ? employee.photo_url : `${API_URL}${employee.photo_url}`}
                        alt={employee.name}
                        style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E2E2E6', flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        data-testid={`planning-employee-photo-${employee.id}`}
                      />
                    ) : (
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#5E5F66', flexShrink: 0 }}>
                        {(employee.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{employee.name}</div>
                      <div style={{ fontSize: '0.7rem', color: '#5E5F66', fontWeight: '400' }}>{employee.job_title || 'Non defini'}</div>
                    </div>
                  </div>
                </td>
                {weekDays.map((day) => {
                  const dayTasks = getTasksForDay(day, employee.id);
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const isDropTarget = dragOverCell === `${employee.id}_${dateStr}`;
                  return (
                    <td
                      key={day.toISOString()}
                      onDragOver={(e) => { e.preventDefault(); setDragOverCell(`${employee.id}_${dateStr}`); }}
                      onDragLeave={() => setDragOverCell(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOverCell(null);
                        try {
                          const payload = JSON.parse(e.dataTransfer.getData('application/json'));
                          if (payload.date === dateStr && payload.user_id === employee.id) return;
                          onTaskDrop(payload.id, dateStr, employee.id);
                        } catch { /* ignore */ }
                      }}
                      style={{
                        verticalAlign: 'top', padding: '8px',
                        backgroundColor: isDropTarget ? '#E8F0FE' : undefined,
                        outline: isDropTarget ? '2px dashed #002FA7' : undefined,
                        transition: 'background-color 0.1s'
                      }}
                    >
                      {dayTasks.map((task) => (
                        <TaskCard key={task.id} task={task} onClick={onTaskClick} />
                      ))}
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ width: '100%', padding: '4px', fontSize: '0.7rem', opacity: 0.6 }}
                        onClick={() => onAddTaskForCell(employee.id, dateStr)}
                      >
                        <Plus size={14} /> Ajouter
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
